// src/components/AsideMenu/AsideMenu.jsx

import React from "react";
import { Link, useLocation } from "react-router-dom";

import {
  FaBell,
  FaBuilding,
  FaChevronLeft,
  FaChevronRight,
  FaClipboardCheck,
  FaLayerGroup,
  FaUsers,
} from "react-icons/fa";

import { MdDashboard } from "react-icons/md";

import { useSidebar } from "../../contexts/SidebarContext";

const MENU_GROUPS = [
  {
    label: "Pilotage",
    items: [
      {
        title: "Tableau de bord",
        path: "/appli/dashboard",
        icon: MdDashboard,
        description: "Vue générale",
      },
    ],
  },
  {
    label: "Gestion",
    items: [
      {
        title: "Utilisateurs",
        path: "/appli/utilisateurs",
        icon: FaUsers,
        description: "Comptes et accès",
      },
      {
        title: "Clubs & Lieux",
        path: "/appli/club-dojo-admin",
        icon: FaBuilding,
        description: "Clubs et dojos",
      },
      {
        title: "Groupes & Adhérents",
        path: "/appli/groupes-adherents",
        icon: FaLayerGroup,
        description: "Effectifs et groupes",
      },
      {
        title: "Appels par les clubs",
        path: "/appli/appels-clubs",
        icon: FaClipboardCheck,
        description: "Suivi des présences",
      },
    ],
  },
  {
    label: "Communication",
    items: [
      {
        title: "Notifications",
        path: "/appli/notifications",
        icon: FaBell,
        description: "Alertes et messages",
      },
    ],
  },
];

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

function isMenuActive(pathname, menuPath) {
  if (menuPath === "/appli/dashboard") {
    return pathname === menuPath;
  }

  return (
    pathname === menuPath ||
    pathname.startsWith(`${menuPath}/`)
  );
}

function MenuItem({
  item,
  pathname,
  isCollapsed,
}) {
  const Icon = item.icon;
  const active = isMenuActive(pathname, item.path);

  return (
    <li className="relative">
      <Link
        to={item.path}
        aria-current={active ? "page" : undefined}
        title={
          isCollapsed
            ? `${item.title} — ${item.description}`
            : undefined
        }
        className={cn(
          "group relative flex rounded-2xl transition-all duration-200",

          isCollapsed
            ? "h-12 items-center justify-center px-2"
            : "items-start gap-3 px-3 py-2.5",

          active
            ? [
                "bg-gradient-to-r from-blue-600 to-sky-500",
                "text-white",
                "shadow-lg shadow-blue-950/25",
                "ring-1 ring-white/10",
              ].join(" ")
            : [
                "text-slate-300",
                "hover:bg-white/[0.07]",
                "hover:text-white",
              ].join(" "),
        )}
      >
        {active ? (
          <span className="absolute -left-1 top-1/2 h-7 w-1 -translate-y-1/2 rounded-r-full bg-sky-300 shadow-[0_0_12px_rgba(125,211,252,0.9)]" />
        ) : null}

        <span
          className={cn(
            "flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-base transition-all",

            !isCollapsed && "mt-px",

            active
              ? "bg-white/15 text-white"
              : [
                  "bg-white/[0.05]",
                  "text-slate-400",
                  "group-hover:bg-white/10",
                  "group-hover:text-sky-300",
                ].join(" "),
          )}
        >
          <Icon />
        </span>

        {!isCollapsed ? (
          <div className="min-w-0 flex-1 pt-px">
            <div
              className={cn(
                "truncate text-[13px] font-bold leading-[17px]",
                active
                  ? "text-white"
                  : "text-slate-200 group-hover:text-white",
              )}
            >
              {item.title}
            </div>

            <div
              className={cn(
                "mt-0.5 truncate text-[10px] font-medium leading-[13px]",

                active
                  ? "text-blue-100"
                  : "text-slate-500 group-hover:text-slate-400",
              )}
            >
              {item.description}
            </div>
          </div>
        ) : null}

        {isCollapsed ? (
          <div className="pointer-events-none absolute left-[calc(100%+12px)] top-1/2 z-50 hidden -translate-y-1/2 whitespace-nowrap rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 shadow-2xl group-hover:block">
            <div className="text-xs font-bold text-white">
              {item.title}
            </div>

            <div className="mt-0.5 text-[10px] text-slate-400">
              {item.description}
            </div>

            <span className="absolute right-full top-1/2 -translate-y-1/2 border-[6px] border-transparent border-r-slate-700" />
          </div>
        ) : null}
      </Link>
    </li>
  );
}

export default function AsideMenu() {
  const {
    isCollapsed,
    setIsCollapsed,
  } = useSidebar();

  const location = useLocation();

  return (
    <aside
      className={cn(
        "fixed bottom-0 left-0 top-[92px] z-40 hidden",
        "border-r border-white/[0.07]",
        "bg-[#0b1022] text-white",
        "shadow-[12px_0_35px_rgba(15,23,42,0.16)]",
        "transition-[width] duration-300 ease-out",
        "lg:flex lg:flex-col",

        isCollapsed
          ? "w-[76px]"
          : "w-[240px]",
      )}
    >
      {/* En-tête */}
      <div
        className={cn(
          "shrink-0 border-b border-white/[0.07]",

          isCollapsed
            ? "px-3 py-4"
            : "px-3 py-4",
        )}
      >
        <div
          className={cn(
            "flex items-center",

            isCollapsed
              ? "justify-center"
              : "justify-between gap-3",
          )}
        >
          {!isCollapsed ? (
            <div className="flex min-w-0 items-center gap-2.5">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-sky-400 text-xs font-black text-white shadow-lg shadow-blue-950/30">
                JP
              </span>

              <div className="min-w-0">
                <div className="truncate text-sm font-black leading-[17px] text-white">
                  Administration
                </div>

                <div className="mt-0.5 truncate text-[10px] leading-[12px] text-slate-500">
                  Judo Présence Pro
                </div>
              </div>
            </div>
          ) : null}

          <button
            type="button"
            onClick={() =>
              setIsCollapsed(!isCollapsed)
            }
            aria-label={
              isCollapsed
                ? "Déployer le menu"
                : "Réduire le menu"
            }
            title={
              isCollapsed
                ? "Déployer le menu"
                : "Réduire le menu"
            }
            className={cn(
              "flex h-9 w-9 shrink-0 items-center justify-center rounded-xl",
              "border border-white/10 bg-white/[0.05]",
              "text-slate-400 transition",
              "hover:border-blue-400/40",
              "hover:bg-blue-500/10",
              "hover:text-sky-300",
            )}
          >
            {isCollapsed ? (
              <FaChevronRight size={12} />
            ) : (
              <FaChevronLeft size={12} />
            )}
          </button>
        </div>
      </div>

      {/* Navigation */}
      <nav className="min-h-0 flex-1 overflow-y-auto overflow-x-visible px-2.5 py-4 [scrollbar-color:rgba(148,163,184,0.25)_transparent] [scrollbar-width:thin]">
        <div className="space-y-5">
          {MENU_GROUPS.map((group) => (
            <section key={group.label}>
              {!isCollapsed ? (
                <div className="mb-1.5 px-2 text-[9px] font-black uppercase tracking-[0.2em] text-slate-600">
                  {group.label}
                </div>
              ) : (
                <div className="mx-auto mb-2.5 h-px w-7 bg-white/10" />
              )}

              <ul className="space-y-1">
                {group.items.map((item) => (
                  <MenuItem
                    key={item.path}
                    item={item}
                    pathname={location.pathname}
                    isCollapsed={isCollapsed}
                  />
                ))}
              </ul>
            </section>
          ))}
        </div>
      </nav>

      {/* État système */}
      <div className="shrink-0 border-t border-white/[0.07] p-2.5">
        {!isCollapsed ? (
          <div className="rounded-2xl border border-white/[0.07] bg-white/[0.04] px-3 py-2.5">
            <div className="flex items-center gap-2.5">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-emerald-500/10">
                <span className="h-2.5 w-2.5 rounded-full bg-emerald-400 shadow-[0_0_12px_rgba(52,211,153,0.7)]" />
              </span>

              <div className="min-w-0">
                <div className="truncate text-[11px] font-bold leading-[14px] text-slate-200">
                  Système opérationnel
                </div>

                <div className="mt-0.5 truncate text-[9px] leading-[11px] text-slate-500">
                  Judo Présence Pro
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div
            className="mx-auto flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-500/10"
            title="Système opérationnel"
          >
            <span className="h-2.5 w-2.5 rounded-full bg-emerald-400 shadow-[0_0_12px_rgba(52,211,153,0.8)]" />
          </div>
        )}
      </div>
    </aside>
  );
}